import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Shield,
  Scale,
  FileText,
  AlertTriangle,
  Users,
  Clock,
  Gavel,
  Lock,
  MessageCircle,
  CheckCircle2,
  ChevronDown,
  HelpCircle,
} from "lucide-react";

const LOGO_ICON = "https://customer-assets.emergentagent.com/job_direito-trabalho-sp/artifacts/8b5v8061_IconOnly_Transparent_NoBuffer.png";
const WHATSAPP_LINK = "https://wa.me/5511917611575?text=Olá! Preciso de orientação em Direito Penal.";

const PenalPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const faqs = [
    {
      pergunta: "Fui chamado para depor na delegacia. Preciso de advogado?",
      resposta: "Sim, é altamente recomendado. Mesmo como testemunha, você tem direito a advogado. Se for como investigado ou indiciado, o advogado é essencial para orientá-lo sobre seus direitos, inclusive o direito ao silêncio, evitando declarações que possam prejudicá-lo."
    },
    {
      pergunta: "Qual a diferença entre inquérito policial e processo criminal?",
      resposta: "O inquérito é a investigação conduzida pela polícia para apurar a autoria e materialidade do crime. O processo criminal é a ação judicial, iniciada pelo Ministério Público, onde há contraditório, ampla defesa e julgamento. No inquérito, o investigado ainda não é réu."
    },
    {
      pergunta: "Posso ser preso antes do julgamento?",
      resposta: "Sim, através da prisão preventiva ou temporária. A preventiva é decretada quando há risco à ordem pública, à investigação ou à aplicação da lei penal. A temporária é cabível durante o inquérito em crimes graves. Ambas podem ser contestadas por habeas corpus."
    },
    {
      pergunta: "O que é habeas corpus e quando posso usar?",
      resposta: "Habeas corpus é o remédio constitucional para proteger a liberdade de locomoção. Pode ser usado quando alguém sofre ou está ameaçado de sofrer prisão ilegal, seja por abuso de autoridade, prisão sem fundamentação adequada ou excesso de prazo."
    },
    {
      pergunta: "Crime de menor potencial ofensivo pode dar prisão?",
      resposta: "Em regra, não resulta em prisão. Crimes com pena máxima de até 2 anos são julgados pelo Juizado Especial Criminal e podem resultar em transação penal (acordo) ou suspensão condicional do processo. Exemplos: lesão corporal leve, ameaça, injúria."
    },
    {
      pergunta: "Antecedentes criminais podem ser apagados?",
      resposta: "Após cumprir a pena, é possível pedir a reabilitação criminal (após 2 anos) para sigilo dos registros. Para casos de absolvição ou extinção da punibilidade, pode-se requerer o cancelamento dos registros. Condenações antigas não devem constar em certidões de antecedentes."
    },
  ];

  const servicos = [
    {
      icon: <Shield className="w-6 h-6" />,
      titulo: "Defesa Criminal",
      descricao: "Atuação em todas as fases do processo criminal, do inquérito ao julgamento em Tribunal do Júri.",
    },
    {
      icon: <Gavel className="w-6 h-6" />,
      titulo: "Habeas Corpus",
      descricao: "Impetração de habeas corpus para garantir a liberdade em casos de prisão ilegal ou abuso de autoridade.",
    },
    {
      icon: <FileText className="w-6 h-6" />,
      titulo: "Recursos Criminais",
      descricao: "Elaboração de recursos (apelação, embargos, recursos especial e extraordinário) para revisão de decisões.",
    },
    {
      icon: <Users className="w-6 h-6" />,
      titulo: "Audiência de Custódia",
      descricao: "Acompanhamento em audiências de custódia para análise da legalidade da prisão em flagrante.",
    },
    {
      icon: <Lock className="w-6 h-6" />,
      titulo: "Execução Penal",
      descricao: "Acompanhamento da execução da pena, pedidos de progressão de regime, livramento condicional e indulto.",
    },
    {
      icon: <Scale className="w-6 h-6" />,
      titulo: "Tribunal do Júri",
      descricao: "Defesa especializada em crimes dolosos contra a vida (homicídio, infanticídio, aborto).",
    },
  ];

  const tiposCrimes = [
    "Crimes contra a pessoa (homicídio, lesão corporal)",
    "Crimes contra o patrimônio (furto, roubo, estelionato)",
    "Crimes contra a honra (calúnia, difamação, injúria)",
    "Crimes de trânsito",
    "Crimes contra a administração pública",
    "Crimes contra a ordem tributária",
    "Crimes ambientais",
    "Lei Maria da Penha",
    "Crimes cibernéticos",
    "Crimes do Código de Defesa do Consumidor",
  ];

  return (
    <>
      <Helmet>
        <title>Advogado Criminalista | Defesa Criminal</title>
        <meta name="description" content="Advogado criminalista especializado. Defesa criminal, habeas corpus, audiência de custódia, recursos e acompanhamento em Tribunal do Júri." />
        <meta name="keywords" content="advogado criminalista, advogado criminal, defesa criminal, habeas corpus, direito penal" />
      </Helmet>

      <div>
        {/* Hero */}
        <section className="pt-32 pb-20 bg-gradient-to-br from-gray-900 to-gray-800 relative overflow-hidden">
          <div className="absolute right-0 top-1/2 -translate-y-1/2 opacity-[0.05] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[500px] h-auto invert" />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <p className="text-red-400 font-medium tracking-wider text-sm mb-4">DIREITO PENAL</p>
            <h1
              className="text-4xl md:text-5xl font-bold text-white mb-6 max-w-3xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Advogado <span className="text-red-400">Criminalista</span>
            </h1>
            <p className="text-lg text-gray-300 mb-8 max-w-2xl">
              Defesa técnica e estratégica em processos criminais. Atuação em todas as fases, 
              do inquérito policial ao Tribunal do Júri, garantindo seus direitos constitucionais.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href={WHATSAPP_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded font-medium transition-all"
              >
                <MessageCircle className="w-5 h-5" />
                Atendimento Urgente
              </a>
              <Link
                to="/contato"
                className="inline-flex items-center justify-center gap-2 border-2 border-gray-600 hover:border-white text-white px-8 py-4 rounded font-medium transition-all"
              >
                Agendar Consulta
              </Link>
            </div>
          </div>
        </section>

        {/* Alerta Urgência */}
        <section className="py-6 bg-red-600">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-center justify-center gap-3 text-white">
              <AlertTriangle className="w-5 h-5" />
              <p className="text-sm font-medium">
                Atendimento 24h para casos urgentes: prisões em flagrante, audiências de custódia e habeas corpus
              </p>
            </div>
          </div>
        </section>

        {/* Serviços */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute left-0 bottom-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Áreas de Atuação em Direito Penal
              </h2>
              <p className="text-gray-600 max-w-2xl mx-auto">
                Defesa técnica e especializada em todas as fases do processo criminal
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {servicos.map((servico, index) => (
                <div key={index} className="bg-white p-6 rounded-xl border border-gray-100 hover:shadow-lg transition-all">
                  <div className="w-12 h-12 bg-gray-900 rounded-lg flex items-center justify-center text-white mb-4">
                    {servico.icon}
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">{servico.titulo}</h3>
                  <p className="text-gray-600 text-sm">{servico.descricao}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Tipos de Crimes */}
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                  Tipos de Crimes que Atuamos
                </h2>
                <p className="text-gray-600 mb-6">
                  Defesa especializada em diversas modalidades de crimes, sempre buscando 
                  a melhor estratégia para cada caso específico.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {tiposCrimes.map((crime, index) => (
                    <div key={index} className="flex items-start gap-2">
                      <CheckCircle2 className="w-4 h-4 text-gray-700 flex-shrink-0 mt-1" />
                      <span className="text-gray-700 text-sm">{crime}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-gray-900 p-8 rounded-xl text-white">
                <Clock className="w-10 h-10 text-red-400 mb-4" />
                <h3 className="text-xl font-semibold mb-4">Atendimento de Urgência</h3>
                <p className="text-gray-300 mb-6">
                  Em casos de prisão em flagrante, oferecemos atendimento imediato para 
                  acompanhamento em audiência de custódia e impetração de habeas corpus.
                </p>
                <ul className="space-y-3 text-gray-300 text-sm mb-6">
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-red-400" />
                    Prisão em flagrante
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-red-400" />
                    Audiência de custódia
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-red-400" />
                    Habeas corpus urgente
                  </li>
                  <li className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-red-400" />
                    Liberdade provisória
                  </li>
                </ul>
                <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded font-medium transition-all w-full">
                  <MessageCircle className="w-5 h-5" />
                  Contato Urgente 24h
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Princípios */}
        <section className="py-20 bg-gray-50 relative overflow-hidden">
          <div className="absolute right-0 top-0 opacity-[0.02] pointer-events-none">
            <img src={LOGO_ICON} alt="" className="w-[400px] h-auto" style={{ filter: "brightness(0)" }} />
          </div>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
            <div className="text-center mb-12">
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Princípios que Norteiam Nossa Atuação
              </h2>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Presunção de Inocência</h3>
                <p className="text-gray-600 text-sm">
                  Todo acusado é inocente até que se prove o contrário. Lutamos para garantir esse direito fundamental.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Scale className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Ampla Defesa</h3>
                <p className="text-gray-600 text-sm">
                  Garantimos que todos os meios de defesa sejam utilizados para proteger os interesses do cliente.
                </p>
              </div>
              <div className="text-center">
                <div className="w-16 h-16 bg-gray-900 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Lock className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Sigilo Profissional</h3>
                <p className="text-gray-600 text-sm">
                  Absoluta confidencialidade em todas as informações compartilhadas pelo cliente.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-20 bg-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <HelpCircle className="w-10 h-10 text-gray-900 mx-auto mb-4" />
              <h2 
                className="text-2xl md:text-3xl font-bold text-gray-900 mb-4"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Perguntas Frequentes
              </h2>
              <p className="text-gray-600">
                Tire suas principais dúvidas sobre Direito Penal
              </p>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <div 
                  key={index}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    onClick={() => setOpenFaq(openFaq === index ? null : index)}
                    className="w-full flex items-center justify-between p-5 text-left bg-white hover:bg-gray-50 transition-colors"
                  >
                    <span className="font-medium text-gray-900 pr-4">{faq.pergunta}</span>
                    <ChevronDown 
                      className={`w-5 h-5 text-gray-500 flex-shrink-0 transition-transform ${
                        openFaq === index ? "rotate-180" : ""
                      }`} 
                    />
                  </button>
                  {openFaq === index && (
                    <div className="px-5 pb-5 pt-0">
                      <p className="text-gray-600 leading-relaxed">{faq.resposta}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-gray-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
              Precisa de um Advogado Criminalista?
            </h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Entre em contato para uma análise sigilosa e especializada do seu caso.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white px-8 py-4 rounded font-medium transition-all">
                <MessageCircle className="w-5 h-5" />
                Falar pelo WhatsApp
              </a>
              <Link to="/contato"
                className="inline-flex items-center justify-center border-2 border-gray-600 hover:border-white text-white px-8 py-4 rounded font-medium transition-all">
                Enviar Mensagem
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default PenalPage;
